import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { identifyClarityUser } from '@/lib/clarity';
import {
  buildUserMetadataFromAttribution,
  getMissingMetadataFields,
  getStoredAttribution,
} from '@/lib/promoAttribution';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  csrfToken: string | null;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getCSRFToken: () => string | null;
  regenerateCSRFToken: () => string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCSRFToken] = useState<string | null>(null);
  const navigate = useNavigate();

  // Generate a cryptographically secure CSRF token
  const generateCSRFToken = (): string => {
    // Use crypto.randomUUID() for secure token generation
    const token = crypto.randomUUID();
    return token;
  };

  // Get current CSRF token
  const getCSRFToken = (): string | null => {
    return csrfToken || sessionStorage.getItem('csrf_token');
  };

  // Regenerate CSRF token (useful after sensitive operations)
  const regenerateCSRFToken = (): string => {
    const newToken = generateCSRFToken();
    setCSRFToken(newToken);
    sessionStorage.setItem('csrf_token', newToken);
    console.log('🔒 CSRF token regenerated');
    return newToken;
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Generate CSRF token when user signs in
        if (event === 'SIGNED_IN' && session) {
          const newToken = generateCSRFToken();
          setCSRFToken(newToken);
          sessionStorage.setItem('csrf_token', newToken);
          console.log('🔒 CSRF token generated on sign in');
        }

        // Clear CSRF token when user signs out
        if (event === 'SIGNED_OUT') {
          setCSRFToken(null);
          sessionStorage.removeItem('csrf_token');
          console.log('🔒 CSRF token cleared on sign out');
        }

        // Regenerate token on token refresh (additional security)
        if (event === 'TOKEN_REFRESHED' && session) {
          const newToken = generateCSRFToken();
          setCSRFToken(newToken);
          sessionStorage.setItem('csrf_token', newToken);
          console.log('🔒 CSRF token regenerated on token refresh');
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      // If session exists, ensure CSRF token exists
      if (session) {
        const existingToken = sessionStorage.getItem('csrf_token');
        if (!existingToken) {
          const newToken = generateCSRFToken();
          setCSRFToken(newToken);
          sessionStorage.setItem('csrf_token', newToken);
          console.log('🔒 CSRF token generated for existing session');
        } else {
          setCSRFToken(existingToken);
        }
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user?.id) {
      identifyClarityUser(user.id);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const attribution = getStoredAttribution();
    if (!attribution) return;

    // If attribution_source already exists (signup or prior backfill), skip entirely.
    if (user.user_metadata?.attribution_source) return;

    const missingFields = getMissingMetadataFields(user.user_metadata, attribution);
    if (Object.keys(missingFields).length === 0) return;

    const dataToUpdate: Record<string, string> = {
      ...missingFields,
    };

    // Mark that attribution came from a login fill, but never override if present
    if (!user.user_metadata?.attribution_source) {
      dataToUpdate.attribution_source = 'login';
    }

    supabase.auth.updateUser({
      data: dataToUpdate,
    }).catch((error) => {
      console.error('Failed to update user metadata with attribution', error);
    });
  }, [user]);

  const signUp = async (email: string, password: string, fullName: string) => {
    const attribution = getStoredAttribution();
    const attributionMetadata = buildUserMetadataFromAttribution(attribution);

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          attribution_source: 'signup',
          ...attributionMetadata,
        },
        emailRedirectTo: `${window.location.origin}/auth`,
      },
    });

    if (error) {
      toast({
        title: 'Sign up failed',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }

    toast({
      title: 'Account created!',
      description: 'You can now sign in to your account.',
    });

    // Don't navigate immediately - let Auth.tsx useEffect handle redirect
    // This ensures onboarding status is checked and user goes to /onboarding if needed
    // navigate('/dashboard');  // REMOVED: This bypassed onboarding flow
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        title: 'Sign in failed',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }

    navigate('/dashboard');
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      toast({
        title: 'Google sign in failed',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signOut = async () => {
    // Clear CSRF token before sign out
    setCSRFToken(null);
    sessionStorage.removeItem('csrf_token');

    // Clear ALL localStorage to prevent data leakage between users
    // This includes selectedWorkspaceId and any other cached state
    localStorage.clear();

    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: 'Sign out failed',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }

    // Force full page reload to clear all React state (Context, Query cache, etc.)
    // This is safer than navigate('/') as it ensures complete state reset
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        csrfToken,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        getCSRFToken,
        regenerateCSRFToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
